import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { PageHeader, LegalSection } from "@/components/PagePrimitives";
import Reveal from "@/components/Reveal";
import NewPrivacyPolicy from "@/components/NewPrivacyPolicy";
import { SITE, THIRD_PARTIES } from "@/config/site";

const EFFECTIVE = "July 2026";

const DATA = [
  ["Name", "To personalise your account.", "Yes"],
  ["Email address", "To create your account, sign you in and send essential emails.", "Yes"],
  ["User ID", "To associate your data with your account.", "Yes"],
  ["Financial information (income, expenses, debts, savings, goals)", "To provide budgeting and planning functionality.", "Yes"],
  ["Uploaded bank statements", "Processed to identify expenses; the original PDF is not permanently stored, though transactions and insights you choose to save remain in your account.", "Yes"],
  ["AI prompts & responses (where retained)", "To provide AI insights and the AI Coach.", "Yes"],
  ["Support requests", "To respond to and resolve your enquiries.", "Yes"],
  ["Purchase & subscription entitlement data", "To confirm and restore Premium access.", "Yes"],
  ["Product interaction data", "To understand how features are used and improve them.", "Yes"],
  ["Crash, performance & diagnostic data", "To diagnose problems and keep Nova reliable.", "Yes"],
];

export default function Privacy() {
  return (
    <>
      <SEO title="Privacy Policy | Nova" description="How Nova collects, uses and protects your information, including account data, financial information and third-party processors." path="/privacy" />
      <PageHeader eyebrow="Legal" title="Nova Privacy Policy" subtitle={`How Nova collects, uses and protects your information. Owned and operated by ${SITE.legalOwner}.`} updated={EFFECTIVE} />

      <section className="pb-24">
        <div className="container-nova max-w-3xl space-y-10">
          <Reveal>
            <NewPrivacyPolicy />
          </Reveal>

          <Reveal>
            <div className="relative flex items-center gap-4 py-2" role="separator" aria-label="Existing Privacy Provisions" data-testid="privacy-divider">
              <span className="h-px flex-1 bg-white/10" />
              <span className="label-eyebrow whitespace-nowrap text-white/50">Existing Privacy Provisions</span>
              <span className="h-px flex-1 bg-white/10" />
            </div>
          </Reveal>

          <Reveal>
            <div className="rounded-2xl border border-nova-cyan/20 bg-nova-cyan/[0.04] p-5 text-sm leading-relaxed text-white/70" data-testid="privacy-intro">
              This policy reflects how Nova, a budgeting and financial-planning app, handles your data in production. It is written to align with Nova's App Store privacy declarations.
            </div>
          </Reveal>

          <LegalSection id="who" heading="1. Who we are & how to contact us">
            <p>Nova is owned and operated by {SITE.legalOwner}. For the purposes of applicable data-protection law, {SITE.legalOwner} is the data controller responsible for the personal information processed through Nova, except where a third-party service acts as an independent controller.</p>
            <p>For any privacy question or request, email <a className="text-nova-cyan hover:underline" href={`mailto:${SITE.supportEmail}`}>{SITE.supportEmail}</a>.</p>
            <p className="text-white/45">Effective date: {EFFECTIVE}.</p>
          </LegalSection>

          <LegalSection id="data" heading="2. Data we collect & why">
            <p>We collect only what we need to provide Nova. The table below sets out each category, why we collect it, and whether it is linked to your account.</p>
            <div className="mt-4 overflow-hidden rounded-2xl border border-white/10">
              <table className="w-full text-left text-sm">
                <thead className="bg-white/[0.03] text-white/70">
                  <tr>
                    <th className="p-3 font-semibold">Data</th>
                    <th className="p-3 font-semibold">Why</th>
                    <th className="p-3 font-semibold">Linked to you</th>
                  </tr>
                </thead>
                <tbody className="text-white/60">
                  {DATA.map(([d, why, linked], i) => (
                    <tr key={i} className="border-t border-white/8">
                      <td className="p-3 text-white/80">{d}</td>
                      <td className="p-3">{why}</td>
                      <td className="p-3">{linked}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </LegalSection>

          <LegalSection id="advertising" heading="3. No targeted advertising & no data sales">
            <p>Nova does not use your personal or financial data for targeted advertising. Nova does not sell your personal or financial information to data brokers or anyone else.</p>
          </LegalSection>

          <LegalSection id="processors" heading="4. Third-party service providers">
            <p>We use a limited set of trusted providers to operate Nova. We only list providers that genuinely receive user data:</p>
            <ul className="mt-3 space-y-2">
              {THIRD_PARTIES.map((p) => (
                <li key={p.name} className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
                  <span className="font-semibold text-white/85">{p.name}</span>: <span className="text-white/55">{p.role}</span>
                </li>
              ))}
            </ul>
            <p className="mt-3 text-white/45">Subscription purchases and verification are handled through the App Store.</p>
          </LegalSection>

          <LegalSection id="retention" heading="5. Data retention">
            <p>We keep your information for as long as your account is active and as needed to provide Nova. When you delete your account, we delete or anonymise your personal data, except where we must retain limited records to meet legal, tax or accounting obligations.</p>
          </LegalSection>

          <LegalSection id="security" heading="6. Security">
            <p>Your information is associated with and protected within your own account, and Nova uses secure authentication. We apply appropriate technical and organisational measures to protect your data. No method of storage or transmission is completely secure, so we cannot guarantee absolute security.</p>
          </LegalSection>

          <LegalSection id="international" heading="7. International processing">
            <p>Your data may be processed by our providers in countries other than your own. Where this happens, we take steps to ensure your information continues to receive appropriate protection.</p>
          </LegalSection>

          <LegalSection id="deletion" heading="8. Account deletion & your rights">
            <p>You can delete your account from inside Nova (Settings). Depending on where you live, you may have rights to access, correct, export or delete your data, and to object to or restrict certain processing. To exercise these rights, contact us or see the <Link to="/delete-account" className="text-nova-cyan hover:underline">Delete Account</Link> page.</p>
          </LegalSection>

          <LegalSection id="children" heading="9. Children's privacy">
            <p>Nova is not intended for children. We do not knowingly collect personal information from children. If you believe a child has provided us data, contact us and we will take appropriate steps.</p>
          </LegalSection>

          <LegalSection id="rights" heading="10. Your rights & complaints">
            <p>Depending on where you live, you may have rights to access, correct, export, delete or restrict processing of your personal data, and to withdraw consent where processing relies on it. If you are in the UK or EU and believe your data has been mishandled, you may lodge a complaint with your data-protection authority. In the UK, that is the Information Commissioner's Office (ICO) at ico.org.uk.</p>
          </LegalSection>

          <LegalSection id="changes" heading="11. Changes to this policy">
            <p>We may update this policy from time to time. We will revise the effective date above and, where appropriate, notify you within the app.</p>
          </LegalSection>

          <Reveal>
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 text-sm text-white/55" data-testid="privacy-contact">
              Questions about your privacy? Email <a className="text-nova-cyan hover:underline" href={`mailto:${SITE.supportEmail}`}>{SITE.supportEmail}</a>.
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
